import React, { ReactElement, useMemo } from 'react';
import { useGetLibraryElearningsQuery } from '@/modules/services/courseLibrary';
import { getQueryString } from '@/lib/util';
import { CourseLibraryDefaultQueryString } from '@/pages/CourseLibrary/CourseLibraryRoutes';
import { useLocation } from 'react-router-dom';
import { Layout, Spinner } from 'elmo-elements';
import CourseLibraryList from '@/pages/CourseLibrary/components/List';
import { CategoryFilter } from '@/components';
import CourseLibraryHeader from '@/components/CourseLibraryHeader';
import NoResult from '@/components/NoModulesResult';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CourseLibraryListV2 from './components/View';
import { CourseLibraryViewData, LibraryElearningsDataType } from './types';
import {
  useGetElearningCategoriesPerTenantQuery,
  useGetLibraryElearningV2Query,
} from '@/modules/services';
import { PaginationType } from '@/modules/types/elearning';

const CourseLibraryView = (): ReactElement => {
  const location = useLocation();
  const { learningElaNewCourseLibraryEnable: isElaCourseLibraryEnabled } =
    useFlags();

  const { data: courseLibraryData, isLoading } = useGetLibraryElearningsQuery(
    getQueryString(location.search, CourseLibraryDefaultQueryString),
    { skip: isElaCourseLibraryEnabled !== false },
  );
  const { data: courseLibraryViewData } = useGetLibraryElearningV2Query(
    getQueryString(location.search, CourseLibraryDefaultQueryString),
    { skip: !isElaCourseLibraryEnabled },
  );
  const { data: elaCategoriesPerTenant } =
    useGetElearningCategoriesPerTenantQuery(undefined, {
      skip: !isElaCourseLibraryEnabled,
    });

  const allowedCategories = useMemo(() => {
    if (courseLibraryData?.allowedCategories) {
      return courseLibraryData.allowedCategories;
    }

    if (elaCategoriesPerTenant?.categories) {
      return elaCategoriesPerTenant.categories.map((category) => ({
        categoryId: category.id,
        categoryName: category.name,
      }));
    }

    return [];
  }, [courseLibraryData, elaCategoriesPerTenant]);

  const shouldShowCategoryFilter = useMemo(() => {
    // For legacy course library
    if (
      courseLibraryData?.allowedCategories &&
      courseLibraryData?.allowedCategories?.length > 0
    ) {
      return true;
    }

    // For new ELA course library
    if (
      courseLibraryViewData &&
      elaCategoriesPerTenant?.categories &&
      elaCategoriesPerTenant?.categories?.length > 0
    ) {
      return true;
    }

    return false;
  }, [courseLibraryData, courseLibraryViewData, elaCategoriesPerTenant]);

  const renderLibraryList = (data: LibraryElearningsDataType | undefined) =>
    data && data.elearnings.length <= 0 ? <NoResult /> : <CourseLibraryList />;
  const renderLibraryListV2 = (
    data: PaginationType<CourseLibraryViewData> | undefined,
  ) => (data && data.data.length <= 0 ? <NoResult /> : <CourseLibraryListV2 />);

  if (
    (!isElaCourseLibraryEnabled && !courseLibraryData) ||
    (isElaCourseLibraryEnabled && !courseLibraryViewData)
  ) {
    return <Spinner />;
  }

  return (
    <>
      <Layout.Header>
        <CourseLibraryHeader title="Course Library" />
      </Layout.Header>
      <Layout.Content>
        {shouldShowCategoryFilter && (
          <CategoryFilter
            allowedCategories={allowedCategories}
            isLibraryDataLoading={isLoading}
            isNewList={isElaCourseLibraryEnabled}
          />
        )}
        {isElaCourseLibraryEnabled
          ? renderLibraryListV2(courseLibraryViewData)
          : renderLibraryList(courseLibraryData)}
      </Layout.Content>
    </>
  );
};

export default CourseLibraryView;
